/**
 * The live portrait's pure parts (requirement 0059, ADR-0127): the WebGL
 * shader, built from the shared fixture so its numbers cannot drift from the
 * app's, and the motion — where the face looks, the breath, the blink.
 *
 * The same shader as mobile/shaders/live_portrait.frag, which the lab tuned on
 * a phone (branch lab/living-portrait). Two layers: the background plate
 * slides as one plane, and the figure is warped by its depth and cut out by
 * its matte, so a turning head uncovers real background rather than smeared
 * hair.
 */
import shader from "../../../testdata/live-portrait/shader.json";

export const SHADER = shader;

const f = (n: number) => (Number.isInteger(n) ? `${n}.0` : `${n}`);

/** The vertex shader: one quad over the whole canvas. */
export const VERTEX = `
attribute vec2 aPos;
void main() { gl_Position = vec4(aPos, 0.0, 1.0); }
`;

/**
 * The fragment shader. WebGL 1: a loop needs a constant bound, and texture
 * rows are uploaded top first, so v grows downwards as the page's y does.
 */
export const FRAGMENT = `
precision highp float;
uniform vec2 uSize;
uniform vec2 uLook;
uniform float uBreath;
uniform float uBlink;
uniform sampler2D uImage;
uniform sampler2D uClosed;
uniform sampler2D uPlate;
uniform sampler2D uDepth;

const float kFocus = ${f(shader.focus)};
const float kStrength = ${f(shader.strength)};
const float kEdge = ${f(shader.edge)};
const float kWall = ${f(shader.wall)};
const float kCrop = ${f(shader.crop)};
const float kDamping = ${f(shader.damping)};
const float kVignette = ${f(shader.vignette)};

void main() {
  vec2 frag = vec2(gl_FragCoord.x, uSize.y - gl_FragCoord.y);
  vec2 uv = (frag / uSize - 0.5) * kCrop + 0.5;
  vec2 shift = uLook * kStrength;

  vec4 wall = texture2D(uPlate, uv + shift * (kWall - kFocus));

  vec2 m = uv;
  float chest = smoothstep(0.58, 1.0, m.y);
  m.y += uBreath * (0.004 + 0.012 * chest);
  m.x = 0.5 + (m.x - 0.5) * (1.0 - uBreath * 0.01 * chest);

  vec2 p = m;
  for (int i = 0; i < ${shader.iterations}; i++) {
    float z = max(texture2D(uDepth, p).r, kEdge);
    p = mix(p, m + shift * (z - kFocus), kDamping);
  }

  vec4 him = mix(texture2D(uImage, p), texture2D(uClosed, p), uBlink);
  float alpha = texture2D(uDepth, p).g;
  vec4 c = mix(wall, him, alpha);

  vec2 v = frag / uSize - 0.5;
  c.rgb *= 1.0 - kVignette * smoothstep(0.35, 0.75, length(v));
  gl_FragColor = vec4(c.rgb, 1.0);
}
`;

const smooth = (x: number) => x * x * (3 - 2 * x);

/**
 * How closed the eyes are, `s` seconds into a blink: quick to close and
 * slower to open, as a person's is.
 */
export function blinkAt(s: number): number {
  const b = shader.blink;
  const close = b.close_ms / 1000;
  const hold = close + b.hold_ms / 1000;
  const open = hold + b.open_ms / 1000;
  if (s < 0) return 0;
  if (s < close) return smooth(s / close);
  if (s < hold) return 1;
  if (s < open) return 1 - smooth((s - hold) / (b.open_ms / 1000));
  return 0;
}

/** The chest's rise and fall, -1..1, `t` seconds in. */
export function breathAt(t: number): number {
  return Math.sin((t / shader.breath_seconds) * 2 * Math.PI);
}

/** Where the face drifts when nobody asks anything of it. */
export function idleAt(t: number): [number, number] {
  const sum = (terms: number[][]) =>
    terms.reduce((acc, [a = 0, w = 0]) => acc + a * Math.sin(t * w), 0);
  return [sum(shader.idle.x), sum(shader.idle.y)];
}

/**
 * Where the face looks for a pointer at (x, y) inside a box of that size:
 * towards it, so the sign is negated — a positive look turns the face away
 * from that side (the lab found this on the phone).
 */
export function lookAt(x: number, y: number, width: number, height: number): [number, number] {
  const clamp = (n: number) => Math.max(-1, Math.min(1, n));
  return [clamp(-(x - width / 2) / (width / 2)), clamp(-(y - height / 2) / (height / 2))];
}

/** Seconds until the next blink: two to six, and now and then straight away again. */
export function nextBlinkGap(random: () => number, justDoubled: boolean): [number, boolean] {
  const b = shader.blink;
  const double = !justDoubled && random() < b.double_chance;
  const gap = double ? 0.32 : b.min_gap_s + random() * (b.max_gap_s - b.min_gap_s);
  return [gap, double];
}
